import type { AppleWebSignUpParams, SocialButtonProps } from 'app/components/authentication/LoginButtonState';
import { SocialAuthButton } from 'app/components/authentication/SocialAuthButton';
import { env } from 'app/lib/env';
import { recordError } from 'app/lib/monitoring';
import { storage, StorageKey } from 'app/lib/storage';
import { type AppleAuthToken } from 'app/types/tokens';
import { jwtDecode } from 'jwt-decode';
import AppleSignin, { appleAuthHelpers, type AppleAuthResponse } from 'react-apple-signin-auth';

const authOptions = {
  clientId: env.APPLE_AUTH_CLIENT_ID,
  redirectURI: env.OAUTH_REDIRECT_URI,
  scope: 'email name',
  usePopup: true,
};

async function getAppleSignUpParams(response: AppleAuthResponse): Promise<AppleWebSignUpParams> {
  const payload = jwtDecode<AppleAuthToken>(response.authorization.id_token);
  const appleParams: AppleWebSignUpParams = {
    email: payload.email,
    token: response.authorization.id_token,
    authorizationCode: response.authorization.code,
    user: payload.sub,
    fullName: {
      givenName: '',
      familyName: '',
    },
    provider: 'Apple',
  };

  const storedAppleUser = await storage.readString(StorageKey.AppleUser);
  if (!storedAppleUser) {
    await storage.saveString(StorageKey.AppleUser, JSON.stringify(response.user));
    appleParams.fullName = {
      givenName: response.user?.name.firstName || '',
      familyName: response.user?.name.lastName || '',
    };
  } else {
    const user = JSON.parse(storedAppleUser);
    appleParams.fullName = {
      givenName: user?.name.firstName || '',
      familyName: user?.name.lastName || '',
    };
  }

  return appleParams;
}

export function AppleLoginButton({ handleSocialIdP, loginButtonsState, iconOnly }: SocialButtonProps) {
  const { setLoginButton } = loginButtonsState;

  const handleSuccess = (response: AppleAuthResponse) => {
    void getAppleSignUpParams(response).then(handleSocialIdP).catch(recordError);
  };

  const handleError = () => {
    setLoginButton('Apple', false);
  };

  const onLoginPress = () => {
    setLoginButton('Apple', true);
    void appleAuthHelpers
      .signIn({
        authOptions,
        onSuccess: handleSuccess,
        onError: handleError,
      })
      .catch(recordError);
  };

  return (
    <AppleSignin
      authOptions={authOptions}
      onError={handleError}
      uiType="dark"
      render={() => (
        <SocialAuthButton
          iconOnly={iconOnly}
          loginButtonsState={loginButtonsState}
          onPress={onLoginPress}
          provider="Apple"
        />
      )}
      onSuccess={handleSuccess}
    />
  );
}
